import { z } from 'zod';

export type OnboardingEventType =
  | 'Onboarding_WelcomeCTAClicked'
  | 'Onboarding_WelcomeOptOutClicked'
  | 'Onboarding_Home_OtherProductsCreateProductClicked'
  | 'Onboarding_Home_ChecklistItemClicked'
  | 'Onboarding_Home_ChecklistCompleteSubmitProductClicked'
  | 'Onboarding_Home_ActiveProductCardCreateFirstProductClicked'
  | 'DirectSeller_SignupLinkGenerated'
  | 'DirectSeller_WelcomeCTAClicked'
  | 'DirectSeller_Connection_ProvidersConnected'
  | 'DirectSeller_Connection_ProvidersError'
  | 'DirectSeller_Connection_SEPAChosen'
  | 'DirectSeller_Connection_AvgPriceChosen'
  | 'DirectSeller_Connection_SalesVolumeChosen'
  | 'DirectSeller_BusinessInfo_Success'
  | 'DirectSeller_BusinessInfo_Error'
  | 'DirectSeller_Documents_Completed';

// Onboarding event payload schemas
export const OnboardingEventBaseSchema = z.object({
  source: z.string(),
  eventContext: z.string().optional(),
  feature: z.string(),
  feature_version: z.string(),
  page: z.string(),
});

export const Onboarding_WelcomeCTAClickedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const Onboarding_WelcomeOptOutClickedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const Onboarding_Home_OtherProductsCreateProductClickedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const Onboarding_Home_ChecklistItemClickedPayloadSchema = OnboardingEventBaseSchema.extend({
  checklistItemKey: z.string(),
});

export const Onboarding_Home_ChecklistCompleteSubmitProductClickedPayloadSchema = OnboardingEventBaseSchema.extend({});

export const Onboarding_Home_ActiveProductCardCreateFirstProductClickedPayloadSchema = OnboardingEventBaseSchema.extend(
  {}
);

// DirectSeller event schemas
export const DirectSeller_SignupLinkGeneratedPayloadSchema = OnboardingEventBaseSchema.extend({
  email: z.string().email(),
  language: z.string(),
  oneTimeKey: z.string(),
  link: z.string().url(),
});

export const DirectSeller_WelcomeCTAClickedPayloadSchema = OnboardingEventBaseSchema.extend({});
export const DirectSeller_Connection_ProvidersConnectedPayloadSchema = OnboardingEventBaseSchema.extend({
  providers: z.array(z.string()),
});

export const DirectSeller_Connection_ProvidersErrorPayloadSchema = OnboardingEventBaseSchema.extend({
  message: z.string(),
});

export const DirectSeller_Connection_SEPAChosenPayloadSchema = OnboardingEventBaseSchema.extend({
  sepa: z.boolean(),
});

export const DirectSeller_Connection_AvgPriceChosenPayloadSchema = OnboardingEventBaseSchema.extend({
  avg_price_option: z.string(),
});

export const DirectSeller_Connection_SalesVolumeChosenPayloadSchema = OnboardingEventBaseSchema.extend({
  sales_volume_option: z.string(),
});

export const DirectSeller_BusinessInfo_SuccessPayloadSchema = OnboardingEventBaseSchema.extend({});

export const DirectSeller_BusinessInfo_ErrorPayloadSchema = OnboardingEventBaseSchema.extend({});

export const DirectSeller_Documents_CompletedPayloadSchema = OnboardingEventBaseSchema.extend({});

export type OnboardingEventTypePayloadMap = {
  Onboarding_WelcomeCTAClicked: z.infer<typeof Onboarding_WelcomeCTAClickedPayloadSchema>;
  Onboarding_WelcomeOptOutClicked: z.infer<typeof Onboarding_WelcomeOptOutClickedPayloadSchema>;
  Onboarding_Home_OtherProductsCreateProductClicked: z.infer<
    typeof Onboarding_Home_OtherProductsCreateProductClickedPayloadSchema
  >;
  Onboarding_Home_ChecklistItemClicked: z.infer<typeof Onboarding_Home_ChecklistItemClickedPayloadSchema>;
  Onboarding_Home_ChecklistCompleteSubmitProductClicked: z.infer<
    typeof Onboarding_Home_ChecklistCompleteSubmitProductClickedPayloadSchema
  >;
  Onboarding_Home_ActiveProductCardCreateFirstProductClicked: z.infer<
    typeof Onboarding_Home_ActiveProductCardCreateFirstProductClickedPayloadSchema
  >;
  DirectSeller_SignupLinkGenerated: z.infer<typeof DirectSeller_SignupLinkGeneratedPayloadSchema>;
  DirectSeller_WelcomeCTAClicked: z.infer<typeof DirectSeller_WelcomeCTAClickedPayloadSchema>;
  DirectSeller_Connection_ProvidersConnected: z.infer<typeof DirectSeller_Connection_ProvidersConnectedPayloadSchema>;
  DirectSeller_Connection_ProvidersError: z.infer<typeof DirectSeller_Connection_ProvidersErrorPayloadSchema>;
  DirectSeller_Connection_SEPAChosen: z.infer<typeof DirectSeller_Connection_SEPAChosenPayloadSchema>;
  DirectSeller_Connection_AvgPriceChosen: z.infer<typeof DirectSeller_Connection_AvgPriceChosenPayloadSchema>;
  DirectSeller_Connection_SalesVolumeChosen: z.infer<typeof DirectSeller_Connection_SalesVolumeChosenPayloadSchema>;
  DirectSeller_BusinessInfo_Success: z.infer<typeof DirectSeller_BusinessInfo_SuccessPayloadSchema>;
  DirectSeller_BusinessInfo_Error: z.infer<typeof DirectSeller_BusinessInfo_ErrorPayloadSchema>;
  DirectSeller_Documents_Completed: z.infer<typeof DirectSeller_Documents_CompletedPayloadSchema>;
};

export const OnboardingEventSchemaMap = {
  Onboarding_WelcomeCTAClicked: Onboarding_WelcomeCTAClickedPayloadSchema,
  Onboarding_WelcomeOptOutClicked: Onboarding_WelcomeOptOutClickedPayloadSchema,
  Onboarding_Home_OtherProductsCreateProductClicked: Onboarding_Home_OtherProductsCreateProductClickedPayloadSchema,
  Onboarding_Home_ChecklistItemClicked: Onboarding_Home_ChecklistItemClickedPayloadSchema,
  Onboarding_Home_ChecklistCompleteSubmitProductClicked:
    Onboarding_Home_ChecklistCompleteSubmitProductClickedPayloadSchema,
  Onboarding_Home_ActiveProductCardCreateFirstProductClicked:
    Onboarding_Home_ActiveProductCardCreateFirstProductClickedPayloadSchema,
  DirectSeller_SignupLinkGenerated: DirectSeller_SignupLinkGeneratedPayloadSchema,
  DirectSeller_WelcomeCTAClicked: DirectSeller_WelcomeCTAClickedPayloadSchema,
  DirectSeller_Connection_ProvidersConnected: DirectSeller_Connection_ProvidersConnectedPayloadSchema,
  DirectSeller_Connection_ProvidersError: DirectSeller_Connection_ProvidersErrorPayloadSchema,
  DirectSeller_Connection_SEPAChosen: DirectSeller_Connection_SEPAChosenPayloadSchema,
  DirectSeller_Connection_AvgPriceChosen: DirectSeller_Connection_AvgPriceChosenPayloadSchema,
  DirectSeller_Connection_SalesVolumeChosen: DirectSeller_Connection_SalesVolumeChosenPayloadSchema,
  DirectSeller_BusinessInfo_Success: DirectSeller_BusinessInfo_SuccessPayloadSchema,
  DirectSeller_BusinessInfo_Error: DirectSeller_BusinessInfo_ErrorPayloadSchema,
  DirectSeller_Documents_Completed: DirectSeller_Documents_CompletedPayloadSchema,
};
